import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button, Container, Row, Col, Form, Input } from "reactstrap";

// This is a soft gate, not a security control.
//
// Everything it guards ships inside the JavaScript bundle that every visitor
// downloads, and so does the password it compares against. Anyone who opens
// devtools, reads the bundle, or disables JavaScript can reach the content
// without ever seeing this prompt. It exists to keep a work-in-progress page
// from being stumbled into, which is what it is good for. Content that must
// genuinely not be read needs server-side auth or needs not to be deployed.
//
// Kept honest by the prop name: nothing here is called `auth`.
const UNLOCK_KEY = "geneGateUnlocked";

// Reading the `sessionStorage` property is itself what throws when the origin
// has no storage access, so the whole access goes inside the try. Same shape as
// BrandMark.js, and for the same reason.
const readUnlocked = (gateId) => {
  try {
    return window.sessionStorage.getItem(`${UNLOCK_KEY}:${gateId}`) === "1";
  } catch (err) {
    // No storage means the reader is asked again on the next navigation. That
    // is the harmless failure; the alternative is defaulting to unlocked.
    return false;
  }
};

const rememberUnlocked = (gateId) => {
  try {
    window.sessionStorage.setItem(`${UNLOCK_KEY}:${gateId}`, "1");
  } catch (err) {
    // Nothing to do. The prompt simply returns on the next page view.
  }
};

function PasswordGate({ gateId, secret, title, prompt, label, submitLabel, errorText, children }) {
  const [unlocked, setUnlocked] = useState(() => readUnlocked(gateId));
  const [value, setValue] = useState("");
  const [failed, setFailed] = useState(false);

  if (unlocked) return children;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (value === secret) {
      rememberUnlocked(gateId);
      setUnlocked(true);
      return;
    }
    setFailed(true);
  };

  return (
    <div className="service-detail">
      {/* The page behind the gate carries its own noindex, but that component
          is not mounted while the gate is closed, so without this a crawler
          would index the prompt itself. Belongs to the gate rather than to the
          page's config: a locked page is never one you want in an index. */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="service-section service-section--hero">
        <Container>
          <Row>
            <Col md="6" className="ml-auto mr-auto">
              <span className="service-rule" aria-hidden="true" />
              <h1 className="service-section-title">{title}</h1>
              <p className="service-section-lede">{prompt}</p>
              <Form onSubmit={handleSubmit}>
                <label htmlFor="gate-password" className="service-prose-heading">
                  {label}
                </label>
                <Input
                  id="gate-password"
                  type="password"
                  value={value}
                  autoFocus
                  autoComplete="off"
                  aria-describedby={failed ? "gate-error" : undefined}
                  aria-invalid={failed ? "true" : undefined}
                  onChange={(event) => {
                    setValue(event.target.value);
                    // Clearing on edit means the message describes the last
                    // attempt, never the one being typed.
                    if (failed) setFailed(false);
                  }}
                />
                {failed && (
                  <p id="gate-error" role="alert" style={{ marginTop: "10px", color: "#B3402F" }}>
                    {errorText}
                  </p>
                )}
                <Button className="btn-fill btn-round mt-3" color="info" type="submit">
                  {submitLabel}
                </Button>
              </Form>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export default PasswordGate;
